import { dmApi } from "../../app/dmApi";
import { serialize } from "../../helpers/Utils";
import { getAuthorizationHeader, handleQueryError, handleQueryErrorAndSuccess } from "../../helpers/RtkQueryUtils";

export const expenseApi = dmApi.injectEndpoints({
    endpoints: (build) => ({
        searchExpenses: build.query({
            query: (data) => {
                let paramData = serialize(data);
                return {
                    url: `/expenses?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['expenses'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        createExpense: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/expense/create`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['expenses'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Expense")
            },
        }),
        getExpenseById: build.query({
            query: (id) => {
                return {
                    url: `/expense/show/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['expenseById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        updateExpense: build.mutation({
            query: ({ id, requestBody }) => {
                const headers = getAuthorizationHeader();
                return {
                    url: `/expense/update/${id}`,
                    method: 'POST',
                    headers,
                    body: requestBody // FormData object
                };
            },
            invalidatesTags: ['expenses', 'expenseById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Expense")
            },
        }),
        removeExpense: build.mutation({
            query: (id) => {
                return {
                    url: `/expense/delete/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Removed", "Expense")
            },
            invalidatesTags: ['expenses']
        }),
        getSuppliersList: build.query({
            query: () => {
                return {
                    url: `/expense/vendors-list`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['suppliersList'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        getSupplierDetails: build.query({
            query: (id) => {
                return {
                    url: `/expense/vendor-details/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['supplierDetails'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        getNextAvailableBillNumber: build.query({
            query: () => {
                return {
                    url: `/expense/next-avl-bill-number`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['nextAvailableBillNumber'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        getExpenses: build.query({
            query: (filters) => {
                let paramData = serialize(filters);
                return {
                    url: `/expense?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['expenses'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getExpenseTypes: build.query({
            query: () => {
                return {
                    url: `/expense/types`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['expenseTypes'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        downloadExpenseInvoice: build.query({
            query: (id) => {
                return {
                    url: `/expense/download/${id}/invoice`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryError(queryFulfilled);
            },
        }),
        uploadExpenseInvoice: build.mutation({
            query: ({ id, requestBody }) => {
                const headers = getAuthorizationHeader();
                return {
                    url: `/expense/upload-invoice/${id}`,
                    method: 'POST',
                    headers,
                    body: requestBody // FormData object
                };
            },
            invalidatesTags: ['expenses', 'expenseById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Expense")
            },
        }),
        uploadExpenseReceipt: build.mutation({
            query: ({ id, requestBody }) => {
                const headers = getAuthorizationHeader();
                return {
                    url: `/expense/upload-receipt/${id}`,
                    method: 'POST',
                    headers,
                    body: requestBody // FormData object
                };
            },
            invalidatesTags: ['expenses', 'expenseById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "Expense")
            },
        }),
        removeExpenseInvoice: build.mutation({
            query: (id) => {
                return {
                    url: `/expense/invoice/delete/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Removed", "Expense")
            },
            invalidatesTags: ['expenses']
        }),
        removeExpenseReceipt: build.mutation({
            query: (id) => {
                return {
                    url: `/expense/receipt/delete/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Removed", "Expense")
            },
            invalidatesTags: ['expenses']
        }),
    }),
});


export const {
    useCreateExpenseMutation,
    useSearchExpensesQuery,
    useUpdateExpenseMutation,
    useRemoveExpenseMutation,
    useGetSuppliersListQuery,
    useGetSupplierDetailsQuery,
    useGetNextAvailableBillNumberQuery,
    useGetExpensesQuery,
    useGetExpenseByIdQuery,
    useGetExpenseTypesQuery,
    useDownloadExpenseInvoiceQuery,
    useUploadExpenseInvoiceMutation,
    useUploadExpenseReceiptMutation,
    useRemoveExpenseInvoiceMutation,
    useRemoveExpenseReceiptMutation
} = expenseApi;